(() => {
  if (window.__sportszoneCardGalleryLoaded) return;
  window.__sportszoneCardGalleryLoaded = true;

  const galleries = new WeakMap();
  const imageCache = new Map();
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const preloadObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const state = galleries.get(entry.target);
          if (!state) return;
          preloadSource(state.sources[1], 'low');
          if (state.sources[2]) {
            window.setTimeout(() => preloadSource(state.sources[2], 'low'), 90);
          }
          preloadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '700px 0px' })
    : null;

  function uniqueSources(gallery) {
    const seen = new Set();
    return [...gallery.querySelectorAll('[data-sz-card-gallery-source]')]
      .map(node => ({
        src: String(node.dataset.src || '').trim(),
        alt: String(node.dataset.alt || '').trim()
      }))
      .filter(item => {
        if (!item.src || seen.has(item.src)) return false;
        seen.add(item.src);
        return true;
      });
  }

  function preloadSource(source, priority = 'low') {
    if (!source?.src) return Promise.resolve(false);
    if (imageCache.has(source.src)) return imageCache.get(source.src);

    const promise = new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      try { img.fetchPriority = priority; } catch (_error) {}
      img.onload = async () => {
        try {
          if (typeof img.decode === 'function') await img.decode();
        } catch (_error) {}
        resolve(true);
      };
      img.onerror = () => resolve(false);
      img.src = source.src;
    });

    imageCache.set(source.src, promise);
    return promise;
  }

  function preloadAround(state, index, priority = 'low') {
    [index + 1, index - 1]
      .filter(i => i > 0 && i < state.sources.length)
      .forEach(i => preloadSource(state.sources[i], priority));
  }

  function makeDots(count) {
    const dots = document.createElement('div');
    dots.className = 'sz-card-gallery__dots';
    dots.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('span');
      dot.className = 'sz-card-gallery__dot';
      if (i === 0) dot.classList.add('is-active');
      dots.appendChild(dot);
    }

    return dots;
  }

  function updateDots(state) {
    state.dots?.querySelectorAll('.sz-card-gallery__dot').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === state.index);
    });
  }

  function ensureOverlay(state) {
    if (state.overlay) return state.overlay;

    const overlay = document.createElement('img');
    overlay.className = 'sz-card-gallery__overlay';
    overlay.alt = '';
    overlay.loading = 'eager';
    overlay.decoding = 'async';
    try { overlay.fetchPriority = 'high'; } catch (_error) {}
    state.gallery.appendChild(overlay);
    state.overlay = overlay;
    return overlay;
  }

  async function showIndex(state, nextIndex) {
    const index = Math.max(0, Math.min(state.sources.length - 1, nextIndex));
    state.index = index;
    updateDots(state);

    const overlay = ensureOverlay(state);

    if (index === 0) {
      overlay.classList.remove('is-visible');
      preloadAround(state, index, 'low');
      return;
    }

    const source = state.sources[index];
    if (!source) return;

    const loaded = await preloadSource(source, 'high');
    if (!loaded || state.index !== index) return;

    if (overlay.dataset.src !== source.src) {
      overlay.classList.remove('is-visible');
      overlay.dataset.src = source.src;
      overlay.alt = source.alt || '';
      overlay.src = source.src;
    }

    requestAnimationFrame(() => {
      if (state.index === index) overlay.classList.add('is-visible');
    });

    preloadAround(state, index, 'low');
  }

  function initGallery(gallery) {
    if (!gallery || galleries.has(gallery)) return;

    const primary = gallery.querySelector('[data-sz-card-gallery-primary], .product-grid-primary__image, .sz-collection-card__image');
    const sources = uniqueSources(gallery);
    if (!primary || sources.length < 2) return;

    const dots = makeDots(sources.length);
    gallery.appendChild(dots);

    const state = {
      gallery,
      primary,
      sources,
      dots,
      overlay: null,
      index: 0,
      startX: 0,
      startY: 0,
      pointerId: null,
      suppressClickUntil: 0
    };

    galleries.set(gallery, state);
    gallery.classList.add('sz-card-gallery--ready');

    if (preloadObserver) {
      preloadObserver.observe(gallery);
    } else {
      window.setTimeout(() => {
        preloadSource(state.sources[1], 'low');
        if (state.sources[2]) window.setTimeout(() => preloadSource(state.sources[2], 'low'), 100);
      }, 180);
    }

    gallery.addEventListener('mouseenter', () => {
      if (!finePointer.matches) return;
      preloadSource(state.sources[1], 'high');
      showIndex(state, 1);
    });

    gallery.addEventListener('mouseleave', () => {
      if (!finePointer.matches) return;
      showIndex(state, 0);
    });

    gallery.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch') return;
      state.pointerId = event.pointerId;
      state.startX = event.clientX;
      state.startY = event.clientY;
      preloadAround(state, state.index, 'high');
      if (state.index === 0 && state.sources[2]) preloadSource(state.sources[2], 'high');
    }, { passive: true });

    gallery.addEventListener('pointerup', event => {
      if (event.pointerType !== 'touch' || state.pointerId !== event.pointerId) return;

      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      state.pointerId = null;

      if (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy) * 1.1) return;

      state.suppressClickUntil = Date.now() + 450;
      showIndex(state, dx < 0 ? state.index + 1 : state.index - 1);
    }, { passive: true });

    gallery.addEventListener('pointercancel', () => {
      state.pointerId = null;
    }, { passive: true });

    gallery.addEventListener('click', event => {
      if (Date.now() <= state.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }

  function init(scope = document) {
    if (scope.matches?.('[data-sz-card-gallery]')) initGallery(scope);
    scope.querySelectorAll?.('[data-sz-card-gallery]').forEach(initGallery);
  }

  document.addEventListener('shopify:section:load', event => init(event.target));

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) init(node);
      });
    });
  });

  const observerRoot = document.getElementById('MainContent') || document.body;
  observer.observe(observerRoot, { childList: true, subtree: true });
  init();
})();